import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import { useOpenRegisterMutation } from "@/redux/service";
import { handleErrors, snakeToCamelAttr } from "@/shared/utils/forms";
import { OpenRegisterFormValues } from "../types";

const openRegisterSchema = (
  t: TFunction,
): yup.ObjectSchema<OpenRegisterFormValues> =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("applications.validation.enterValidEmail"))
      .typeError(t("applications.validation.emailNotValid"))
      .required(t("applications.validation.enterValidEmail")),
    firstName: yup
      .string()
      .typeError(t("applications.validation.firstNameNotValid"))
      .required(t("applications.validation.firstNameRequired")),
    lastName: yup
      .string()
      .typeError(t("applications.validation.lastNameNotValid"))
      .required(t("applications.validation.lastNameRequired")),
    password: yup
      .string()
      .typeError(t("auth.validation.passwordNotValid"))
      .min(8, t("auth.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("auth.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("auth.validation.passwordNumberValidation"))
      .required(t("auth.validation.passwordRequired")),
    confirmPassword: yup
      .string()
      .typeError(t("auth.validation.passwordNotValid"))
      .oneOf([yup.ref("password")], t("auth.validation.passwordsMustMatch"))
      .required(t("auth.validation.passwordRequired")),
  });

export const useOpenRegister = () => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const [openRegister, { isLoading: isSubmitting }] = useOpenRegisterMutation();

  const form = useForm<OpenRegisterFormValues>({
    resolver: yupResolver(openRegisterSchema(t)),
    mode: "onChange",
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
  });

  const startOpenRegistration = (email: string) => {
    form.setValue("email", email);
  };

  const onValidSubmit = ({
    email,
    password,
    firstName,
    lastName,
  }: OpenRegisterFormValues) => {
    openRegister({ email, password, firstName, lastName })
      .unwrap()
      .then(() => setRegisteredEmail(email))
      .catch((errors) =>
        handleErrors(
          errors,
          form.setError,
          notification,
          undefined,
          snakeToCamelAttr,
        ),
      );
  };

  return {
    form,
    registeredEmail,
    isSubmitting,
    startOpenRegistration,
    handleSubmit: form.handleSubmit(onValidSubmit),
  };
};
